import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useEncryptedParams } from "../../hooks/useEncryptedParams";
import { encrypt } from "../../utils/encryption";
import apiClient from "../../services/apiClient";
import { normalizeApiError } from "../../utils/apiError";
import Button from "../../components/common/Button";
import Input from "../../components/common/Input";
import TextArea from "../../components/common/TextArea";
import ErrorAlert from "../../components/common/ErrorAlert";
import CrudTable from "../../components/common/crud/CrudTable";
import useCrud from "../../components/common/crud/useCrud";
import alertService from "../../utils/alertService";

function CompanyModulePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useEncryptedParams();
  
  // --- States ---
  const [formData, setFormData] = useState({
    companyName: "",
    companyAddress: "",
    companyGSTNo: "",
    stateName: "",
    stateCode: "",
    email: "",
    websiteLink: "",
    isActive: true,
  });
  const [formErrors, setFormErrors] = useState({});
  const [isLoadingEdit, setIsLoadingEdit] = useState(false);
  const [formError, setFormError] = useState(null);

  const isCreatePage = location.pathname.endsWith("/new") || location.pathname.endsWith("/create");
  const isEditPage = Boolean(id);
  const isFormPage = isCreatePage || isEditPage;
  const formMode = isEditPage ? "edit" : "create";

  // Base path for navigation within this module
  const basePath = useMemo(() => {
    if (location.pathname.includes("/settings-master/company")) return "/settings-master/company";
    return "/company";
  }, [location.pathname]);

  const endpoints = useMemo(
    () => ({
      list: "/company",
      delete: (id) => `/company/${id}`,
    }),
    []
  );

  const {
    rows,
    isLoading,
    isSubmitting: isListLoading,
    error: listError,
    filters,
    pagination,
    sortConfig,
    askDelete,
    changePage,
    changeSort,
    updateFilters,
    refresh,
  } = useCrud({
    endpoints,
    idKey: "id",
    initialFilters: { search: "" },
    initialSort: { key: "companyName", order: "ASC" },
  });

  const [isSaving, setIsSaving] = useState(false);

  // --- Initial Load for Edit ---
  useEffect(() => {
    if (!isEditPage || !id) {
      setFormData({
        companyName: "",
        companyAddress: "",
        companyGSTNo: "",
        stateName: "",
        stateCode: "",
        email: "",
        websiteLink: "",
        isActive: true,
      });
      setFormErrors({});
      setFormError(null);
      return;
    }

    let isMounted = true;
    const loadEditData = async () => {
      setIsLoadingEdit(true);
      setFormError(null);
      try {
        const response = await apiClient.get(`/company/${id}`);
        const payload = response?.data || {};
        const data = payload?.data || payload || {};
        if (!isMounted) return;
        setFormData({
          companyName: data.companyName || "",
          companyAddress: data.companyAddress || "",
          companyGSTNo: data.companyGSTNo || "",
          stateName: data.stateName || "",
          stateCode: data.stateCode || "",
          email: data.email || "",
          websiteLink: data.websiteLink || "",
          isActive: data.isActive !== false,
        });
      } catch (err) {
        if (!isMounted) return;
        const normalized = normalizeApiError(err);
        setFormError(normalized.message);
      } finally {
        if (isMounted) setIsLoadingEdit(false);
      }
    };

    loadEditData();
    return () => {
      isMounted = false;
    };
  }, [id, isEditPage]);

  // --- Handlers ---
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const validate = () => {
    const errors = {};
    if (!formData.companyName.trim()) errors.companyName = "Company Name is required";
    if (!formData.companyAddress.trim()) errors.companyAddress = "Address is required";
    
    // GST Validation
    const gstPattern = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
    if (!formData.companyGSTNo.trim()) {
      errors.companyGSTNo = "GST No is required";
    } else if (!gstPattern.test(formData.companyGSTNo.trim())) {
      errors.companyGSTNo = "Invalid GST format (e.g. 27AAAAA0000A1Z5)";
    }

    if (!formData.stateName.trim()) errors.stateName = "State Name is required";
    if (!formData.stateCode.trim()) errors.stateCode = "State Code is required";
    else if (formData.stateCode.trim().length > 2) errors.stateCode = "State Code must be 2 characters";

    // Email Validation
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      errors.email = "Email is required";
    } else if (!emailPattern.test(formData.email.trim())) {
      errors.email = "Invalid email format";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async (e) => {
    if (e) e.preventDefault();
    if (!validate()) return;

    setIsSaving(true);
    setFormError(null);
    try {
      if (formMode === "edit" && id) {
        await apiClient.patch(`/company/${id}`, formData);
        alertService.success("Company updated successfully.");
      } else {
        await apiClient.post("/company", formData);
        alertService.success("Company created successfully.");
      }
      await refresh();
      navigate(basePath, { replace: true });
    } catch (err) {
      const normalized = normalizeApiError(err);
      setFormError(normalized.message);
      alertService.error(normalized.message);
    } finally {
      setIsSaving(false);
    }
  };

  const openCreate = () => navigate(`${basePath}/new`);
  const openEdit = (item) => navigate(`${basePath}/${encrypt(item?.id)}/edit`);

  const tableColumns = useMemo(
    () => [
      { key: "companyName", label: "Company Name", sortable: true },
      { key: "email", label: "Email", sortable: true },
      { key: "companyGSTNo", label: "GST No", sortable: true },
      { key: "stateName", label: "State", sortable: true },
      {
        key: "status",
        label: "Status",
        render: (item) => (
          <span
            className={`rounded-full px-2 py-1 text-xs font-semibold ${
              item?.isActive
                ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300"
                : "bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300"
            }`}
          >
            {item?.isActive ? "Active" : "Inactive"}
          </span>
        ),
      },
    ],
    []
  );

  if (isFormPage) {
    const notFound = formMode === "edit" && !isLoadingEdit && !formData.companyName && !formError;

    return (
      <div className="space-y-3 max-w-4xl mx-auto">
        <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-800 pb-4">
          <div>
            <h1 className="text-2xl font-black text-slate-800 dark:text-white uppercase tracking-tight">
              {formMode === "edit" ? "Edit" : "Create"} <span className="text-primary-600">Company</span>
            </h1>
            <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mt-1">
              Settings Master / Company / {formMode === "edit" ? "Edit" : "New"}
            </p>
          </div>
          <Button variant="secondary" onClick={() => navigate(basePath)}>
            <svg className="mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to List
          </Button>
        </div>

        {formError ? <ErrorAlert message={formError} /> : null}
        
        {notFound ? (
          <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:bg-rose-900/20 dark:border-rose-800">
            Record not found.
          </div>
        ) : (
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900 overflow-hidden">
            <form onSubmit={handleSave}>
              <table className="w-full border-collapse">
                <tbody>
                  {/* --- Company Name --- */}
                  <tr className="border-b border-slate-100 dark:border-slate-800">
                    <td className="w-1/3 py-4 px-6 text-[11px] font-black uppercase tracking-widest text-slate-500 bg-slate-50/50 dark:bg-slate-800/50">
                      Company Name <span className="text-danger">*</span>
                    </td>
                    <td className="py-3 px-6">
                      <Input
                        name="companyName"
                        value={formData.companyName}
                        onChange={handleInputChange}
                        error={formErrors.companyName}
                        placeholder="e.g. Ferrovant Global"
                      />
                    </td>
                  </tr>

                  {/* --- Email --- */}
                  <tr className="border-b border-slate-100 dark:border-slate-800">
                    <td className="w-1/3 py-4 px-6 text-[11px] font-black uppercase tracking-widest text-slate-500 bg-slate-50/50 dark:bg-slate-800/50">
                      Email Address <span className="text-danger">*</span>
                    </td>
                    <td className="py-3 px-6">
                      <Input
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        error={formErrors.email}
                        placeholder="info@company.com"
                      />
                    </td>
                  </tr>

                  {/* --- GST No --- */}
                  <tr className="border-b border-slate-100 dark:border-slate-800">
                    <td className="w-1/3 py-4 px-6 text-[11px] font-black uppercase tracking-widest text-slate-500 bg-slate-50/50 dark:bg-slate-800/50">
                      GST Number <span className="text-danger">*</span>
                    </td>
                    <td className="py-3 px-6">
                      <Input
                        name="companyGSTNo"
                        value={formData.companyGSTNo}
                        onChange={handleInputChange}
                        error={formErrors.companyGSTNo}
                        placeholder="27AAAAA0000A1Z5"
                      />
                    </td>
                  </tr>

                  {/* --- State Details --- */}
                  <tr className="border-b border-slate-100 dark:border-slate-800">
                    <td className="w-1/3 py-4 px-6 text-[11px] font-black uppercase tracking-widest text-slate-500 bg-slate-50/50 dark:bg-slate-800/50">
                      State Name & Code <span className="text-danger">*</span>
                    </td>
                    <td className="py-3 px-6">
                      <div className="flex gap-4">
                        <div className="flex-1">
                          <Input
                            name="stateName"
                            value={formData.stateName}
                            onChange={handleInputChange}
                            error={formErrors.stateName}
                            placeholder="e.g. Maharashtra"
                          />
                        </div>
                        <div className="w-24">
                          <Input
                            name="stateCode"
                            value={formData.stateCode}
                            onChange={handleInputChange}
                            error={formErrors.stateCode}
                            placeholder="Code"
                            maxLength={2}
                          />
                        </div>
                      </div>
                    </td>
                  </tr>

                  {/* --- Address --- */}
                  <tr className="border-b border-slate-100 dark:border-slate-800">
                    <td className="w-1/3 py-4 px-6 text-[11px] font-black uppercase tracking-widest text-slate-500 bg-slate-50/50 dark:bg-slate-800/50">
                      Company Address <span className="text-danger">*</span>
                    </td>
                    <td className="py-3 px-6">
                      <TextArea
                        name="companyAddress"
                        value={formData.companyAddress}
                        onChange={handleInputChange}
                        error={formErrors.companyAddress}
                        placeholder="Full office address..."
                        rows={3}
                      />
                    </td>
                  </tr>

                  {/* --- Website --- */}
                  <tr className="border-b border-slate-100 dark:border-slate-800">
                    <td className="w-1/3 py-4 px-6 text-[11px] font-black uppercase tracking-widest text-slate-500 bg-slate-50/50 dark:bg-slate-800/50">
                      Website Link
                    </td>
                    <td className="py-3 px-6">
                      <Input
                        name="websiteLink"
                        value={formData.websiteLink}
                        onChange={handleInputChange}
                        error={formErrors.websiteLink}
                        placeholder="https://www.company.com"
                      />
                    </td>
                  </tr>

                  {/* --- Status --- */}
                  <tr>
                    <td className="w-1/3 py-4 px-6 text-[11px] font-black uppercase tracking-widest text-slate-500 bg-slate-50/50 dark:bg-slate-800/50">
                      Status
                    </td>
                    <td className="py-3 px-6">
                      <label className="flex cursor-pointer items-center gap-2">
                        <input
                          type="checkbox"
                          name="isActive"
                          checked={formData.isActive}
                          onChange={handleInputChange}
                          className="h-4 w-4 rounded border-slate-300 text-primary-600 focus:ring-primary-600/20"
                        />
                        <span className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase">
                          {formData.isActive ? "Active" : "Inactive"}
                        </span>
                      </label>
                    </td>
                  </tr>
                </tbody>
              </table>

              <div className="bg-slate-50 dark:bg-slate-800/30 p-6 flex items-center gap-3">
                <Button
                  type="submit"
                  variant="primary"
                  isLoading={isSaving}
                  className="px-8 shadow-lg shadow-primary-500/20"
                >
                  {formMode === "edit" ? "Update Company" : "Save Company"}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => navigate(basePath)}
                  disabled={isSaving}
                  className="px-6"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-slate-800 dark:text-white uppercase tracking-tight">
            Company <span className="text-primary-600">Master</span>
          </h1>
          <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mt-1">
            Manage your organizations and branch details
          </p>
        </div>
        <Button onClick={openCreate} className="shadow-xl shadow-primary-500/10">
          <svg className="mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Create Company
        </Button>
      </div>

      {listError ? <ErrorAlert message={listError} /> : null}

      <CrudTable
        columns={tableColumns}
        rows={rows}
        isLoading={isLoading}
        pagination={pagination}
        sortConfig={sortConfig}
        filters={filters}
        onFiltersChange={updateFilters}
        onSort={changeSort}
        onPageChange={changePage}
        onEdit={openEdit}
        onDelete={askDelete}
        searchKey="search"
        searchPlaceholder="Search by name, email or GST"
        tableScrollHeight="58vh"
      />
    </div>
  );
}

export default CompanyModulePage;
